import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Wallet } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { mintWalletJwt } from '@/lib/auth';
import { ApiError } from '@/lib/fetcher';
import { useSession } from '@/contexts/SessionContext';

/**
 * Wallet-JWT signin. Three steps:
 *
 *   1. Browser EIP-1193 provider exposes accounts → walletAddress.
 *   2. /v1/auth/challenge?wallet=... → { nonce, message, expiresAt }.
 *   3. provider.request({ method: 'personal_sign', params: [message,
 *      walletAddress] }) → signature.
 *   4. /v1/auth/verify { walletAddress, nonce, signature } →
 *      { token, expiresIn }.
 *   5. Stash JWT in sessionStorage; navigate.
 *
 * No viem/wagmi dependency for the basic flow — `personal_sign` via
 * `window.ethereum.request` is the canonical EIP-191 path and works
 * with every injected EVM wallet without a 100KB connector library.
 */

interface EthereumProvider {
  request: (args: {
    method: string;
    params?: unknown[];
  }) => Promise<unknown>;
  isMetaMask?: boolean;
}

declare global {
  interface Window {
    ethereum?: EthereumProvider;
  }
}

type Stage = 'idle' | 'connecting' | 'signing' | 'verifying' | 'error';

export function WalletSignIn() {
  const navigate = useNavigate();
  const { seed } = useSession();
  const [searchParams] = useSearchParams();
  const next = searchParams.get('next') ?? '/dashboard';

  const [stage, setStage] = React.useState<Stage>('idle');
  const [error, setError] = React.useState<string | null>(null);
  const [walletAddress, setWalletAddress] = React.useState<string | null>(null);

  const start = async () => {
    setError(null);
    if (!window.ethereum) {
      setError(
        'No EVM wallet detected. Install MetaMask, Rabby, or Coinbase Wallet and try again.',
      );
      setStage('error');
      return;
    }
    try {
      setStage('connecting');
      const accounts = (await window.ethereum.request({
        method: 'eth_requestAccounts',
      })) as string[];
      const addr = accounts[0];
      if (!addr) throw new Error('No account returned by wallet.');
      setWalletAddress(addr);

      setStage('signing');
      const result = await mintWalletJwt({
        walletAddress: addr,
        signMessage: async (message) => {
          const signature = (await window.ethereum!.request({
            method: 'personal_sign',
            params: [message, addr],
          })) as string;
          return signature;
        },
      });

      setStage('verifying');
      // mintWalletJwt already wrote sessionStorage; seed the context
      // with the wallet identity so /dashboard renders without a probe
      // round-trip.
      seed({ authType: 'wallet', walletAddress: addr });
      void result;
      navigate(next, { replace: true });
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'wallet_signin_failed';
      const message =
        err instanceof Error ? err.message : 'Wallet sign-in failed.';
      setError(`${code}: ${message}`);
      setStage('error');
    }
  };

  return (
    <main className="container flex min-h-screen items-center justify-center py-16">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            Sign in with wallet
          </CardTitle>
          <CardDescription>
            We'll ask your wallet to sign a one-time challenge. This proves
            you own the address and gives Relaystation a JWT for the next 24
            hours. No transactions are sent.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {walletAddress ? (
            <p className="text-sm text-muted-foreground">
              Address:{' '}
              <code className="font-mono text-xs">{walletAddress}</code>
            </p>
          ) : null}
          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Couldn't sign in</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <Button
            className="w-full"
            onClick={start}
            disabled={stage === 'connecting' || stage === 'signing' ||
              stage === 'verifying'}
          >
            {stage === 'connecting'
              ? 'Connecting wallet…'
              : stage === 'signing'
                ? 'Waiting for signature…'
                : stage === 'verifying'
                  ? 'Verifying…'
                  : 'Connect & sign'}
          </Button>
          <Button
            variant="ghost"
            className="w-full gap-2"
            onClick={() => navigate('/signin')}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to sign-in options
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}
